import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Alert,
  Chip,
  Stack,
  Divider,
  Paper,
} from '@mui/material';
import {
  HealthAndSafety as HealthIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  Insights as InsightsIcon,
  Assignment as AssignmentIcon,
  SmartToy as AIIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';

export const AnalyticsIndexNew: React.FC = () => {
  const navigate = useNavigate();

  const sections = [
    {
      title: '🤖 Site Health Check',
      subtitle: 'AI-powered overview of your website performance',
      description: 'Get a comprehensive health score and understand what\'s working and what needs attention',
      path: '/analytics/attention',
      icon: <HealthIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
      aiPreview: 'Your site health score: 78/100. 3 issues need attention, 5 opportunities identified.',
      metrics: [
        { label: 'Health Score', value: '78/100', color: 'warning' },
        { label: 'Issues', value: '3 critical', color: 'error' },
        { label: 'Opportunities', value: '5 found', color: 'success' },
      ],
    },
    {
      title: '🚨 What Needs Attention?',
      subtitle: 'Pages and issues requiring immediate action',
      description: 'AI prioritizes problems by impact and urgency, with clear explanations and guidance',
      path: '/analytics/attention',
      icon: <WarningIcon sx={{ fontSize: 48, color: 'error.main' }} />,
      aiPreview: '8 pages with declining traffic. Your homepage lost 200 clicks/day - I\'ll show you why.',
      metrics: [
        { label: 'Critical', value: '3 pages', color: 'error' },
        { label: 'Warnings', value: '5 pages', color: 'warning' },
        { label: 'Est. Impact', value: '-500 clicks/day', color: 'default' },
      ],
    },
    {
      title: '🎯 Growth Strategy',
      subtitle: 'AI-identified opportunities to increase traffic',
      description: 'Pages close to page 1, content gaps, and quick wins with estimated value',
      path: '/analytics/opportunities',
      icon: <TrendingUpIcon sx={{ fontSize: 48, color: 'success.main' }} />,
      aiPreview: '12 pages could reach page 1 with minor improvements. Start with these 3 for +500 visitors/month.',
      metrics: [
        { label: 'Quick Wins', value: '12 pages', color: 'success' },
        { label: 'Potential', value: '+500 visitors', color: 'success' },
        { label: 'Effort', value: 'Low-Medium', color: 'info' },
      ],
    },
    {
      title: '📊 Performance Insights',
      subtitle: 'What\'s working and what trends AI has detected',
      description: 'Understand patterns, seasonal trends, and content performance across your site',
      path: '/analytics/momentum',
      icon: <InsightsIcon sx={{ fontSize: 48, color: 'info.main' }} />,
      aiPreview: 'Your blog content is up 25%, but product pages need work. I\'ve identified 3 patterns...',
      metrics: [
        { label: 'Trending Up', value: '15 pages', color: 'success' },
        { label: 'Stable', value: '42 pages', color: 'info' },
        { label: 'Declining', value: '8 pages', color: 'warning' },
      ],
    },
    {
      title: '✅ Next Steps',
      subtitle: 'Your AI-organized action plan',
      description: 'Prioritized tasks with step-by-step guidance and impact estimates',
      path: '/actions',
      icon: <AssignmentIcon sx={{ fontSize: 48, color: 'secondary.main' }} />,
      aiPreview: 'Here\'s your weekly plan: 5 tasks prioritized by ROI. Estimated time: 4 hours.',
      metrics: [
        { label: 'This Week', value: '5 tasks', color: 'primary' },
        { label: 'Est. Time', value: '4 hours', color: 'default' },
        { label: 'Est. Impact', value: 'High', color: 'success' },
      ],
    },
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box display="flex" flexDirection="column" gap={4}>
        {/* Header */}
        <Box>
          <Typography variant="h3" component="h1" gutterBottom>
            Analytics Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            AI-powered insights to help you understand and improve your website performance
          </Typography>
          
          <Alert 
            severity="info" 
            icon={<AIIcon />}
            sx={{ mt: 2 }}
          >
            <Typography variant="body2">
              <strong>AI Advisory Mode:</strong> Each section includes AI-generated insights, explanations, and recommendations 
              tailored to your website. Click any card to explore detailed analysis and get personalized guidance.
            </Typography>
          </Alert>
        </Box>

        {/* Advisory Sections */}
        <Grid container spacing={3}>
          {sections.map((section, index) => (
            <Grid item xs={12} key={index}>
              <Card 
                elevation={2}
                sx={{ 
                  height: '100%',
                  transition: 'all 0.3s',
                  '&:hover': {
                    elevation: 8,
                    transform: 'translateY(-4px)',
                  }
                }}
              >
                <CardActionArea 
                  onClick={() => navigate(section.path)}
                  sx={{ height: '100%' }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box display="flex" gap={3}>
                      {/* Icon */}
                      <Box sx={{ flexShrink: 0 }}>
                        {section.icon}
                      </Box>

                      {/* Content */}
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h5" gutterBottom>
                          {section.title}
                        </Typography>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          {section.subtitle}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {section.description}
                        </Typography>

                        <Divider sx={{ my: 2 }} />

                        {/* AI Preview */}
                        <Paper 
                          variant="outlined" 
                          sx={{ 
                            p: 2, 
                            bgcolor: 'grey.50',
                            borderLeft: 3,
                            borderColor: 'primary.main',
                            mb: 2,
                          }}
                        >
                          <Box display="flex" alignItems="flex-start" gap={1}>
                            <AIIcon sx={{ fontSize: 20, color: 'primary.main', mt: 0.5 }} />
                            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                              "{section.aiPreview}"
                            </Typography>
                          </Box>
                        </Paper>

                        {/* Metrics */}
                        <Stack direction="row" spacing={2} flexWrap="wrap">
                          {section.metrics.map((metric, idx) => (
                            <Chip
                              key={idx}
                              label={`${metric.label}: ${metric.value}`}
                              color={metric.color as any}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Stack>
                      </Box>

                      {/* Arrow */}
                      <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                        <ArrowIcon color="action" />
                      </Box>
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Footer Note */}
        <Alert severity="success" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Coming Soon:</strong> Interactive AI chat within each section - ask questions, 
            get explanations, and receive personalized recommendations in real-time.
          </Typography>
        </Alert>
      </Box>
    </Container>
  );
};

export default AnalyticsIndexNew;
